import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import { supabase } from "@/integrations/supabase/client";

interface CaseStats {
  month: string;
  completed: number;
  inProgress: number;
}

export function StatisticsSection() {
  const [data, setData] = useState<CaseStats[]>([]);

  useEffect(() => {
    async function fetchCaseStats() {
      const { data: cases, error } = await supabase
        .from('cases')
        .select('status, created_at')
        .gte('created_at', new Date(new Date().setMonth(new Date().getMonth() - 6)).toISOString());

      if (error) {
        console.error('Error fetching case statistics:', error);
        return;
      }

      // Process cases into monthly statistics
      const monthlyStats = cases.reduce((acc: { [key: string]: CaseStats }, curr) => {
        const date = new Date(curr.created_at);
        const monthKey = date.toLocaleString('no', { month: 'short' });
        
        if (!acc[monthKey]) {
          acc[monthKey] = {
            month: monthKey,
            completed: 0,
            inProgress: 0
          };
        }

        if (curr.status === 'completed') {
          acc[monthKey].completed += 1;
        } else {
          acc[monthKey].inProgress += 1;
        }

        return acc;
      }, {});

      setData(Object.values(monthlyStats));
    }

    fetchCaseStats();
  }, []);

  return (
    <Card className="overflow-hidden h-full">
      <CardHeader className="space-y-1 p-4 md:p-6">
        <CardTitle className="text-lg md:text-xl font-semibold break-words">
          Saksstatistikk
        </CardTitle>
      </CardHeader>
      <CardContent className="p-4 md:p-6">
        <div className="w-full h-[200px] md:h-[300px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={data} margin={{ top: 5, right: 30, bottom: 5, left: 5 }}>
              <XAxis 
                dataKey="month" 
                fontSize={12}
                tickMargin={8}
              />
              <YAxis 
                fontSize={12}
                tickMargin={8}
              />
              <Tooltip 
                contentStyle={{ 
                  fontSize: '12px',
                  padding: '8px',
                  border: 'none',
                  borderRadius: '4px',
                  backgroundColor: 'rgba(255, 255, 255, 0.9)'
                }}
              />
              <Legend />
              <Bar 
                name="Fullførte saker" 
                dataKey="completed" 
                fill="#22c55e" 
                radius={[4, 4, 0, 0]} 
              />
              <Bar 
                name="Pågående saker" 
                dataKey="inProgress" 
                fill="#f59e0b" 
                radius={[4, 4, 0, 0]} 
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
}